import { detectInstanceSoftware } from './instance_detection';

interface BackendLinks {
  preferencesLink?: string;
  profileLink?: string;
  signOutLink: string;
  privacyPolicyLink?: string;
  accountAdminLink?: (id: string) => string | undefined;
  statusAdminLink?: (
    account_id: string,
    status_id: string,
  ) => string | undefined;
  filterEditLink?: (id: string) => string | undefined;
  filtersLink?: string | undefined;
  mutesLink?: string | undefined;
  blocksLink?: string | undefined;
  domainBlocksLink?: string | undefined;
  privacyLink?: string | undefined;
  statusesCleanupLink?: string | undefined;
  exportLink?: string | undefined;
  adminReportsLink?: string | undefined;
  adminDashboardLink?: string | undefined;
  relationshipsLink?: string | undefined;
  securityLink?: string | undefined;
}

let cachedLinks: BackendLinks | null = null;

export async function getBackendLinks(): Promise<BackendLinks> {
  if (cachedLinks) {
    return cachedLinks;
  }

  const domain = localStorage.getItem('domain');

  if (!domain) {
    cachedLinks = {
      signOutLink: '/auth/sign_out',
    };
    return cachedLinks;
  }

  const software = await detectInstanceSoftware(`https://${domain}`);
  const getBackendUrl = (path: string) => `https://${domain}${path}`;

  const isMastodon = software === 'mastodon' || software === 'glitch';
  const isGoToSocial = software === 'gotosocial';
  const isIceshrimpNET = software === 'iceshrimp.net';

  cachedLinks = {
    preferencesLink: getBackendUrl(
      isMastodon ? '/settings/preferences' : '/settings',
    ),
    profileLink: getBackendUrl(
      isGoToSocial ? '/settings/user/profile' : '/settings/profile',
    ),
    // The standalone logout page (served at the clean URL by the host).
    signOutLink: '/logout',
    privacyPolicyLink: getBackendUrl(
      isMastodon
        ? '/privacy-policy'
        : isIceshrimpNET
          ? '/settings/about'
          : '/about',
    ),
    accountAdminLink: isMastodon
      ? (id: string) => getBackendUrl(`/admin/accounts/${id}`)
      : () => undefined,
    statusAdminLink: isMastodon
      ? (account_id: string, status_id: string) =>
          getBackendUrl(`/admin/accounts/${account_id}/statuses/${status_id}`)
      : () => undefined,
    filterEditLink: isMastodon
      ? (id: string) => getBackendUrl(`/filters/${id}/edit`)
      : () => undefined,
    filtersLink: isMastodon ? getBackendUrl('/filters') : undefined,
    mutesLink: isMastodon ? getBackendUrl('/mutes') : undefined,
    blocksLink: isMastodon ? getBackendUrl('/blocks') : undefined,
    domainBlocksLink: isMastodon ? getBackendUrl('/domain_blocks') : undefined,
    privacyLink: getBackendUrl(
      isMastodon
        ? '/settings/privacy'
        : isGoToSocial
          ? '/settings/user/posts'
          : isIceshrimpNET
            ? '/settings/account'
            : '/settings',
    ),
    statusesCleanupLink: isMastodon
      ? getBackendUrl('/statuses_cleanup')
      : undefined,
    exportLink: getBackendUrl(
      isMastodon
        ? '/settings/export'
        : isGoToSocial
          ? '/settings/user/export-import'
          : isIceshrimpNET
            ? '/settings/import-export'
            : '/settings',
    ),
    adminReportsLink: isMastodon
      ? getBackendUrl('/admin/reports')
      : isGoToSocial
        ? getBackendUrl('/settings/moderation/reports')
        : isIceshrimpNET
          ? getBackendUrl('/mod/reports')
          : undefined,
    adminDashboardLink: isMastodon
      ? getBackendUrl('/admin/dashboard')
      : isIceshrimpNET
        ? getBackendUrl('/mod/overview')
        : undefined,
    relationshipsLink: isMastodon ? getBackendUrl('/relationships') : undefined,
    securityLink: isMastodon ? getBackendUrl('/auth/edit') : undefined,
  };

  return cachedLinks;
}
